import type { CookieAttributes } from 'js-cookie';

export type AccessToken = string;
export type RefreshToken = string;

export enum Method {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export type Headers = Record<string, string | number | boolean>;
export type Params = Record<string, string | number>;
export type Query = Record<string, string> | URLSearchParams | string;
export type Body = Record<string, unknown> | FormData;
export type Result = unknown;

export type RequestDetails = {
  readonly url: string;
  readonly method: Method;
  readonly params: Params;
  readonly query: Query;
  readonly body: Body;
  readonly withCredentials?: boolean;
  readonly withAuthorization: boolean;
};

export type Response = {
  readonly result: any;
  readonly status: number;
  readonly message?: string;
  readonly details: RequestDetails;
};

export type CommonRequestOptions<R extends Result> = {
  readonly headers?: Headers;
  readonly withAuthorization: boolean;
  readonly withCredentials?: boolean;
  readonly responseMiddleware?: (response: Response) => Promise<R> | R;
  readonly errorMiddleware?: (error: unknown) => Promise<never> | never;
};

export type RequestOptions<
  M extends Method,
  R extends Result,
  P extends Params | never,
  Q extends Query | never,
  B extends Body | never,
> =
  M extends Method.GET
    ? [P] extends [never]
      ? [Q] extends [never]
        ? CommonRequestOptions<R>
        : CommonRequestOptions<R> & { readonly query: Q; }
      : [Q] extends [never]
        ? CommonRequestOptions<R> & { readonly params: P; }
        : CommonRequestOptions<R> & { readonly query: Q; readonly params: P; }
    : [P] extends [never]
      ? [Q] extends [never]
        ? [B] extends [never]
          ? CommonRequestOptions<R>
          : CommonRequestOptions<R> & { readonly body: B; }
        : [B] extends [never]
          ? CommonRequestOptions<R> & { readonly query: Q; }
          : CommonRequestOptions<R> & { readonly query: Q; readonly body: B; }
      : [Q] extends [never]
        ? [B] extends [never]
          ? CommonRequestOptions<R> & { readonly params: P; }
          : CommonRequestOptions<R> & { readonly params: P; readonly body: B; }
        : [B] extends [never]
          ? CommonRequestOptions<R> & { readonly params: P; readonly query: Q; }
          : CommonRequestOptions<R> & { readonly params: P; readonly query: Q, readonly body: B; };

export type GetOptions<R extends Result, P extends Params | never, Q extends Query | never> = Omit<RequestOptions<Method.GET, R, P, Q, never>, 'method'>;
export type PostOptions<R extends Result, P extends Params | never, Q extends Query | never, B extends Body> = Omit<RequestOptions<Method.POST, R, P, Q, B>, 'method'>;
export type PutOptions<R extends Result, P extends Params | never, Q extends Query | never, B extends Body> = Omit<RequestOptions<Method.PUT, R, P, Q, B>, 'method'>;
export type PatchOptions<R extends Result, P extends Params | never, Q extends Query | never, B extends Body> = Omit<RequestOptions<Method.PATCH, R, P, Q, B>, 'method'>;
export type DeleteOptions<R extends Result, P extends Params | never, Q extends Query | never, B extends Body> = Omit<RequestOptions<Method.DELETE, R, P, Q, B>, 'method'>;

export type Config = {
  readonly urls: {
    readonly authorize: string;
    readonly token: string;
    readonly logout: string;
  };
  readonly baseURL?: string;
  readonly clientId: string;
  readonly cookie: Omit<CookieAttributes, 'domain'> & {
    readonly domain: string;
  };
  readonly basicResponseMiddleware: <R extends Result>(response: Response) => Promise<R> | R;
  readonly basicErrorMiddleware: (error: unknown) => Promise<never> | never;
};

export type CodeAuthorization = {
  readonly state: string;
  readonly code: string;
};

export type Introspect = Record<string, unknown>;
